import { useState, useMemo } from 'react';
import { Button } from './ui/button';

const API_BASE = "https://kiss-node-orchestrator-1057209991970.us-central1.run.app";

// ─── Inline article content fetcher (mirrors MonitoringPage ArticleContent) ──

function ArticleContent({ url }) {
  const [state, setState]     = useState('idle');
  const [content, setContent] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetch_ = async () => {
    setState('loading');
    try {
      const res = await fetch(`${API_BASE}/fetchContent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const data = await res.json();
      if (data.content) { setContent(data.content); setState('loaded'); setExpanded(true); }
      else setState(data.status || 'unavailable');
    } catch { setState('error'); }
  };

  const label = { idle:'Read Article', loading:'Loading…', loaded: expanded ? 'Collapse' : 'Expand',
    unavailable:'Unavailable', paywall:'Paywalled', error:'Error' }[state] ?? 'Read Article';

  return (
    <div>
      <button
        onClick={state === 'loaded' ? () => setExpanded(e => !e) : state === 'idle' ? fetch_ : undefined}
        disabled={['loading','unavailable','paywall','error'].includes(state)}
        className="text-xs text-blue-400 hover:text-blue-300 disabled:text-gray-600 disabled:cursor-not-allowed"
      >
        {label}
      </button>
      {expanded && content && (
        <div className="mt-2 text-xs text-gray-300 bg-gray-800 rounded p-3 max-h-48 overflow-y-auto leading-relaxed whitespace-pre-wrap">
          {content.slice(0, 3000)}{content.length > 3000 ? '…' : ''}
        </div>
      )}
    </div>
  );
}

// ISO 3166-1 alpha-2 → readable name for common codes
const COUNTRY_NAMES = {
  US: 'United States', GB: 'United Kingdom', RU: 'Russia', CN: 'China',
  DE: 'Germany', FR: 'France', UA: 'Ukraine', IL: 'Israel', IR: 'Iran',
  SY: 'Syria', TR: 'Turkey', SA: 'Saudi Arabia', IN: 'India', PK: 'Pakistan',
  KP: 'North Korea', KR: 'South Korea', JP: 'Japan', AU: 'Australia',
  CA: 'Canada', BR: 'Brazil', MX: 'Mexico', ZA: 'South Africa', EG: 'Egypt',
  IQ: 'Iraq', AF: 'Afghanistan', LY: 'Libya', YE: 'Yemen', VE: 'Venezuela',
  BY: 'Belarus', PL: 'Poland', SE: 'Sweden', NO: 'Norway', FI: 'Finland',
};

function countryName(code) {
  return COUNTRY_NAMES[code] || code;
}

function fmtDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function toneColor(tone) {
  if (tone === null || tone === undefined) return 'text-gray-500';
  if (tone < -2) return 'text-red-400';
  if (tone < 0)  return 'text-orange-400';
  if (tone < 2)  return 'text-gray-300';
  return 'text-green-400';
}

// ─── Spike detection ─────────────────────────────────────────────────────────

function annotateSpikes(timeline, windowWeeks = 12, multiplier = 2) {
  if (!timeline || timeline.length < 3) return timeline;
  return timeline.map((bucket, i) => {
    const window = timeline.slice(Math.max(0, i - windowWeeks), i);
    if (window.length < 3) return { ...bucket, spike: false };
    const avg = window.reduce((s, b) => s + b.count, 0) / window.length;
    return { ...bucket, spike: avg > 0 && bucket.count >= avg * multiplier, baseline: avg };
  });
}

// ─── Inline SVG bar chart ────────────────────────────────────────────────────

function BarChart({ data, xKey, yKey, height = 100 }) {
  if (!data || data.length === 0) return <p className="text-gray-600 text-xs italic">No data</p>;
  const maxVal = Math.max(...data.map(d => d[yKey]), 1);
  const W = Math.max(data.length * 18, 200);
  // Find first spike for annotation
  const firstSpike = data.findIndex(d => d.spike);
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${height}`} style={{ height, width: '100%', minWidth: W }} preserveAspectRatio="none">
        {data.map((d, i) => {
          const barH = Math.max(Math.round((d[yKey] / maxVal) * (height - 12)), d[yKey] > 0 ? 2 : 0);
          const fill = d.spike ? '#facc15' : '#22c55e';
          return (
            <g key={i}>
              <rect x={i * 18 + 1} y={height - 12 - barH} width={16} height={barH} fill={fill} opacity="0.8" />
              {i === firstSpike && (
                <line x1={i * 18 + 9} y1={0} x2={i * 18 + 9} y2={height - 12} stroke="#facc15" strokeWidth="1" strokeDasharray="3,2" opacity="0.5" />
              )}
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-xs text-gray-600 mt-1">
        <span>{data[0]?.[xKey]}</span>
        {data.length > 2 && <span>{data[Math.floor(data.length / 2)]?.[xKey]}</span>}
        <span>{data[data.length - 1]?.[xKey]}</span>
      </div>
      <div className="flex gap-4 mt-1">
        <span className="text-xs text-green-400">█ coverage</span>
        <span className="text-xs text-yellow-400">█ spike (2× baseline)</span>
      </div>
    </div>
  );
}

// ─── Tone over time — dot plot on a signed axis ──────────────────────────────

function ToneChart({ data }) {
  if (!data || data.length === 0) return <p className="text-gray-600 text-xs italic">No data</p>;
  const maxAbs = Math.max(...data.map(d => Math.abs(d.avg_tone)), 1);
  const H = 80;
  const midY = H / 2;
  const W = Math.max(data.length * 18, 200);
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ height: H, width: '100%', minWidth: W }} preserveAspectRatio="none">
        {/* zero line */}
        <line x1={0} y1={midY} x2={W} y2={midY} stroke="#374151" strokeWidth="1" />
        {data.map((d, i) => {
          const cx = i * 18 + 9;
          const cy = midY - (d.avg_tone / maxAbs) * (midY - 6);
          const fill = d.avg_tone < 0 ? '#f87171' : '#4ade80';
          return <circle key={i} cx={cx} cy={cy} r={4} fill={fill} opacity="0.8" />;
        })}
        {/* connect dots */}
        {data.map((d, i) => {
          if (i === 0) return null;
          const prev = data[i - 1];
          const x1 = (i - 1) * 18 + 9;
          const y1 = midY - (prev.avg_tone / maxAbs) * (midY - 6);
          const x2 = i * 18 + 9;
          const y2 = midY - (d.avg_tone / maxAbs) * (midY - 6);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#4b5563" strokeWidth="1" />;
        })}
      </svg>
      <div className="flex justify-between text-xs text-gray-600 mt-1">
        <span>{data[0]?.week}</span>
        {data.length > 2 && <span>{data[Math.floor(data.length / 2)]?.week}</span>}
        <span>{data[data.length - 1]?.week}</span>
      </div>
      <div className="flex gap-4 mt-1">
        <span className="text-xs text-green-400">● positive</span>
        <span className="text-xs text-red-400">● negative</span>
      </div>
    </div>
  );
}

// ─── Horizontal bar ──────────────────────────────────────────────────────────

function HBar({ label, sublabel, count, maxCount }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-36 shrink-0">
        <div className="text-xs text-green-400 truncate">{label}</div>
        {sublabel && <div className="text-xs text-gray-600 truncate">{sublabel}</div>}
      </div>
      <div className="flex-1 bg-gray-800 rounded-full h-2">
        <div
          className="bg-green-500 h-2 rounded-full"
          style={{ width: `${Math.max((count / maxCount) * 100, 1)}%` }}
        />
      </div>
      <span className="text-xs text-gray-400 w-8 text-right tabular-nums">{count}</span>
    </div>
  );
}

// ─── Wayback Machine archive date checker ────────────────────────────────────

function ArchiveDate({ url }) {
  const [state, setState] = useState('idle'); // idle | loading | found | not_found | error
  const [date, setDate]   = useState(null);

  const check = async () => {
    setState('loading');
    try {
      const res = await fetch(
        `https://web.archive.org/cdx/search/cdx?url=${encodeURIComponent(url)}&output=json&limit=1&fl=timestamp`,
        { signal: AbortSignal.timeout(10000) }
      );
      const data = await res.json();
      // Response: [["timestamp"], ["YYYYMMDDHHMMSS"]] — first row is header
      if (Array.isArray(data) && data.length >= 2 && data[1]?.[0]) {
        const ts = String(data[1][0]);
        const d = new Date(`${ts.slice(0,4)}-${ts.slice(4,6)}-${ts.slice(6,8)}T00:00:00Z`);
        setDate(d.toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric', timeZone:'UTC' }));
        setState('found');
      } else {
        setState('not_found');
      }
    } catch {
      setState('error');
    }
  };

  if (state === 'idle') return (
    <button onClick={check} className="text-xs text-gray-700 hover:text-gray-500 underline">
      check archive
    </button>
  );
  if (state === 'loading') return <span className="text-xs text-gray-700">checking…</span>;
  if (state === 'found') return (
    <span className="text-xs text-gray-600">
      first archived:{' '}
      <a href={`https://web.archive.org/web/*/${url}`} target="_blank" rel="noopener noreferrer"
         className="text-blue-500 hover:text-blue-400">{date}</a>
    </span>
  );
  if (state === 'not_found') return <span className="text-xs text-gray-700">not in archive</span>;
  return <span className="text-xs text-gray-700">archive unavailable</span>;
}

// ─── Main ResearchPage ────────────────────────────────────────────────────────

export default function ResearchPage() {
  const [query, setQuery]       = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [results, setResults]       = useState(null);
  const [eventResults, setEventResults]   = useState(null);
  const [wikiResults, setWikiResults]     = useState(null);
  const [tvResults, setTvResults]         = useState(null);
  const [gkgFtResults, setGkgFtResults]   = useState(null);
  const [trendsResults, setTrendsResults] = useState(null);
  const [secResults, setSecResults]       = useState(null);
  const [wikiLeaksResults, setWikiLeaksResults] = useState(null);
  const [docCloudResults, setDocCloudResults]   = useState(null);
  const [hnResults, setHnResults]               = useState(null);
  const [arxivResults, setArxivResults]         = useState(null);
  const [fourchanResults, setFourchanResults]   = useState(null);
  const [githubResults, setGithubResults]       = useState(null);
  const [blueskyResults, setBlueskyResults]     = useState(null);
  const [redditResults, setRedditResults]       = useState(null);
  const [lemmyResults, setLemmyResults]         = useState(null);
  const [youtubeResults, setYoutubeResults]     = useState(null);
  const [fedRegResults, setFedRegResults]       = useState(null);
  const [archiveResults, setArchiveResults]     = useState(null);
  const [cveResults, setCveResults]             = useState(null);

  // Extract keywords from query text for events search (simple word extraction)
  const extractKeywords = (text) =>
    text.match(/\b[A-Z][a-zA-Z]{2,}\b/g)?.slice(0, 5) || [];

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setResults(null);
    setEventResults(null);
    setWikiResults(null);
    setTvResults(null);
    setGkgFtResults(null);
    setTrendsResults(null);
    setSecResults(null);
    setWikiLeaksResults(null);
    setDocCloudResults(null);
    setHnResults(null);
    setArxivResults(null);
    setFourchanResults(null);
    setGithubResults(null);
    setBlueskyResults(null);
    setRedditResults(null);
    setLemmyResults(null);
    setYoutubeResults(null);
    setFedRegResults(null);
    setArchiveResults(null);
    setCveResults(null);

    const keywords = extractKeywords(query);
    const body = { query: query.trim(), dateFrom: dateFrom || null, dateTo: dateTo || null };
    const post = (path, b) => fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(b),
    });

    try {
      const [probeS, eventsS, wikiS, tvS, gkgFtS, trendsS, secS, wlS, dcS, hnS, axS, fcS, ghS, bsS, rdS, lmS, ytS, frS, arS, cvS] = await Promise.allSettled([
        post('/researchProbe', { ...body, limit: 500 }),
        keywords.length > 0 ? post('/researchEvents', { keywords, dateFrom: body.dateFrom, dateTo: body.dateTo }) : Promise.resolve(null),
        post('/researchWikipedia', { query: body.query }),
        post('/researchTV', body),
        post('/researchGKGFullText', body),
        post('/researchTrends', body),
        post('/researchSEC', { query: body.query }),
        post('/researchWikiLeaks', { query: body.query }),
        post('/researchDocumentCloud', { query: body.query }),
        post('/researchHN', { query: body.query }),
        post('/researchArXiv', { query: body.query }),
        post('/research4chan', { query: body.query }),
        post('/researchGitHub', { query: body.query }),
        post('/researchBluesky', { query: body.query }),
        post('/researchReddit', { query: body.query }),
        post('/researchLemmy', { query: body.query }),
        post('/researchYouTube', { query: body.query }),
        post('/researchFedReg', { query: body.query }),
        post('/researchInternetArchive', { query: body.query }),
        post('/researchCVE', { query: body.query }),
      ]);

      // Probe is required
      if (probeS.status === 'rejected') throw new Error('Search failed');
      const probeData = await probeS.value.json();
      if (!probeS.value.ok) throw new Error(probeData.error || 'Search failed');
      setResults(probeData);

      // All others are optional — fail silently
      if (eventsS.status === 'fulfilled' && eventsS.value) {
        const eventsData = await eventsS.value.json();
        if (eventsS.value.ok) setEventResults(eventsData);
      }
      if (wikiS.status === 'fulfilled') {
        const wikiData = await wikiS.value.json();
        if (wikiS.value.ok && wikiData.results?.length) setWikiResults(wikiData);
      }
      if (tvS.status === 'fulfilled') {
        const tvData = await tvS.value.json();
        if (tvS.value.ok && tvData.timeline?.length) setTvResults(tvData);
      }
      if (gkgFtS.status === 'fulfilled') {
        const gkgFtData = await gkgFtS.value.json();
        if (gkgFtS.value.ok && gkgFtData.timeline?.length) setGkgFtResults(gkgFtData);
      }
      if (trendsS.status === 'fulfilled') {
        const trendsData = await trendsS.value.json();
        if (trendsS.value.ok && trendsData.timeline?.length) setTrendsResults(trendsData);
      }
      if (secS.status === 'fulfilled') {
        const secData = await secS.value.json();
        if (secS.value.ok && secData.results?.length) setSecResults(secData);
      }
      if (wlS.status === 'fulfilled') {
        const wlData = await wlS.value.json();
        if (wlS.value.ok && wlData.results?.length) setWikiLeaksResults(wlData);
      }
      if (dcS.status === 'fulfilled') {
        const dcData = await dcS.value.json();
        if (dcS.value.ok && dcData.results?.length) setDocCloudResults(dcData);
      }
      if (hnS.status === 'fulfilled') {
        const hnData = await hnS.value.json();
        if (hnS.value.ok && hnData.results?.length) setHnResults(hnData);
      }
      if (axS.status === 'fulfilled') {
        const axData = await axS.value.json();
        if (axS.value.ok && axData.results?.length) setArxivResults(axData);
      }
      if (fcS.status === 'fulfilled') {
        const fcData = await fcS.value.json();
        if (fcS.value.ok && fcData.results?.length) setFourchanResults(fcData);
      }
      if (ghS.status === 'fulfilled') {
        const ghData = await ghS.value.json();
        if (ghS.value.ok && ghData.results?.length) setGithubResults(ghData);
      }
      if (bsS.status === 'fulfilled') {
        const bsData = await bsS.value.json();
        if (bsS.value.ok && bsData.results?.length) setBlueskyResults(bsData);
      }
      if (rdS.status === 'fulfilled') {
        const rdData = await rdS.value.json();
        if (rdS.value.ok && rdData.results?.length) setRedditResults(rdData);
      }
      if (lmS.status === 'fulfilled') {
        const lmData = await lmS.value.json();
        if (lmS.value.ok && lmData.results?.length) setLemmyResults(lmData);
      }
      if (ytS.status === 'fulfilled') {
        const ytData = await ytS.value.json();
        if (ytS.value.ok && ytData.results?.length) setYoutubeResults(ytData);
      }
      if (frS.status === 'fulfilled') {
        const frData = await frS.value.json();
        if (frS.value.ok && frData.results?.length) setFedRegResults(frData);
      }
      if (arS.status === 'fulfilled') {
        const arData = await arS.value.json();
        if (arS.value.ok && arData.results?.length) setArchiveResults(arData);
      }
      if (cvS.status === 'fulfilled') {
        const cvData = await cvS.value.json();
        if (cvS.value.ok && cvData.results?.length) setCveResults(cvData);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const maxCountry = useMemo(() =>
    results ? Math.max(...(results.byCountry || []).map(c => c.count), 1) : 1,
    [results]
  );
  const maxDomain = useMemo(() =>
    results ? Math.max(...(results.byDomain || []).map(d => d.count), 1) : 1,
    [results]
  );

  // Spike-annotated timeline
  const annotatedTimeline = useMemo(() =>
    annotateSpikes(results?.timeline || []),
    [results]
  );

  const firstSpikeWeek = useMemo(() =>
    annotatedTimeline.find(b => b.spike)?.week || null,
    [annotatedTimeline]
  );

  // Related entities — aggregate from earliest records' themes/persons/orgs
  // (research-probe returns these on earliest records)
  const relatedEntities = useMemo(() => {
    if (!results?.earliest) return { persons: [], orgs: [], themes: [] };
    const personCounts = {}, orgCounts = {}, themeCounts = {};
    for (const r of results.earliest) {
      for (const p of (r.persons || [])) {
        const k = p.trim(); if (k) personCounts[k] = (personCounts[k] || 0) + 1;
      }
      for (const o of (r.orgs || [])) {
        const k = o.trim(); if (k) orgCounts[k] = (orgCounts[k] || 0) + 1;
      }
      for (const t of (r.themes || [])) {
        const k = t.replace(/_/g, ' ').toLowerCase().trim();
        if (k) themeCounts[k] = (themeCounts[k] || 0) + 1;
      }
    }
    const rank = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]).slice(0, 10);
    return { persons: rank(personCounts), orgs: rank(orgCounts), themes: rank(themeCounts) };
  }, [results]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-green-400 mb-1">Narrative Research</h2>
        <p className="text-sm text-gray-400">
          Trace the origin and spread of a topic or narrative across global news coverage using GDELT data (2013–present).
        </p>
      </div>

      {/* Query form */}
      <div className="border border-gray-700 rounded p-4 bg-gray-900 space-y-3">
        <div>
          <label className="text-xs text-gray-400 block mb-1">Topic or narrative *</label>
          <textarea
            className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-green-400 text-sm placeholder-gray-500 resize-none font-mono"
            rows={3}
            placeholder="Describe the narrative, topic, or story in plain language — e.g. 'Russian military activity near Ukrainian border' or 'AI chip export controls targeting China'"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-4 flex-wrap">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Date from (optional)</label>
            <input
              type="date"
              className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-green-400 text-sm"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Date to (optional)</label>
            <input
              type="date"
              className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-green-400 text-sm"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
          </div>
        </div>
        {error && <p className="text-red-400 text-xs">{error}</p>}
        <Button onClick={handleSearch} disabled={loading || !query.trim()}>
          {loading ? 'Searching…' : 'Probe Narrative'}
        </Button>
        {loading && (
          <p className="text-xs text-gray-500 italic">Embedding query and searching ~92M records — this takes 10–20 seconds…</p>
        )}
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-8">
          <p className="text-xs text-gray-500">
            {results.total} semantically similar records found across GDELT coverage
          </p>

          {/* ── Wikipedia Coverage ── */}
          {wikiResults && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Wikipedia coverage
                <span className="text-gray-600 font-normal ml-2">— when did this topic become notable enough for Wikipedia?</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {wikiResults.results.map((r, i) => (
                  <div key={i} className="border border-gray-700 rounded p-3 bg-gray-900">
                    <a href={r.url} target="_blank" rel="noopener noreferrer"
                       className="text-sm text-blue-400 hover:text-blue-300 font-medium block mb-1 truncate">
                      {r.title}
                    </a>
                    {r.created && (
                      <div className="text-xs text-green-400 mb-1">Article created: {fmtDate(r.created)}</div>
                    )}
                    {r.wordCount > 0 && (
                      <div className="text-xs text-gray-600 mb-1">{r.wordCount.toLocaleString()} words</div>
                    )}
                    {r.snippet && (
                      <div className="text-xs text-gray-500 leading-relaxed">
                        {r.snippet.slice(0, 160)}{r.snippet.length > 160 ? '…' : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── First Seen ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 mb-3">
              First seen
              <span className="text-gray-600 font-normal ml-2">— earliest records matching this narrative</span>
            </h3>
            {results.earliest && results.earliest.length > 0 ? (
              <div className="space-y-2">
                {results.earliest.map((r, i) => (
                  <div key={i} className="border border-gray-700 rounded p-3 bg-gray-900 flex gap-3 items-start">
                    <div className="shrink-0 text-center">
                      <div className="text-green-400 text-sm font-mono font-semibold">{fmtDate(r.date)}</div>
                      <div className="text-xs text-gray-600">sim {r.similarity}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      {r.url ? (
                        <div>
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-400 hover:text-blue-300 truncate block"
                          >
                            {r.url}
                          </a>
                          <ArchiveDate url={r.url} />
                        </div>
                      ) : (
                        <span className="text-xs text-gray-600 italic">no URL</span>
                      )}
                      {r.sources.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {r.sources.map((s, j) => (
                            <span key={j} className="text-xs text-gray-500 border border-gray-700 rounded px-1">{s}</span>
                          ))}
                        </div>
                      )}
                      {r.themes.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {r.themes.map((t, j) => (
                            <span key={j} className="text-xs text-gray-700">{t.replace(/_/g, ' ').toLowerCase()}</span>
                          ))}
                        </div>
                      )}
                      {r.url && (
                        <div className="mt-1">
                          <ArticleContent url={r.url} />
                        </div>
                      )}
                    </div>
                    {r.tone !== null && (
                      <span className={`text-xs shrink-0 ${toneColor(r.tone)}`}>
                        {r.tone > 0 ? '+' : ''}{r.tone.toFixed(1)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-xs italic">No dated records found</p>
            )}
          </div>

          {/* ── Recent Feed Matches ── */}
          {results.feedItems && results.feedItems.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Recent feed matches
                <span className="text-gray-600 font-normal ml-2">— live sources from the past weeks</span>
              </h3>
              <div className="space-y-2">
                {results.feedItems.slice(0, 10).map((item, i) => (
                  <div key={i} className="border border-gray-700 rounded p-3 bg-gray-900 flex gap-3 items-start">
                    <div className="shrink-0 text-center">
                      <div className="text-green-400 text-xs font-mono">{fmtDate(item.published_at)}</div>
                      <div className="text-xs text-gray-600">sim {item.similarity}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      {item.url ? (
                        <a href={item.url} target="_blank" rel="noopener noreferrer"
                           className="text-xs text-blue-400 hover:text-blue-300 truncate block">
                          {item.title || item.url}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">{item.title}</span>
                      )}
                      {item.source && (
                        <span className="text-xs text-gray-600 mt-0.5 block">{item.source}</span>
                      )}
                    </div>
                    {item.tone !== null && (
                      <span className={`text-xs shrink-0 ${toneColor(item.tone)}`}>
                        {item.tone > 0 ? '+' : ''}{parseFloat(item.tone).toFixed(1)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Volume Timeline ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 mb-3">
              Coverage volume
              <span className="text-gray-600 font-normal ml-2">— weekly, by date of publication</span>
            </h3>
            <div className="bg-gray-900 border border-gray-700 rounded p-4">
              <BarChart data={annotatedTimeline} xKey="week" yKey="count" height={100} />
              <div className="flex justify-between mt-2">
                <span className="text-xs text-gray-600">
                  peak {Math.max(...(results.timeline || []).map(t => t.count), 0)} articles/week
                </span>
                {firstSpikeWeek && (
                  <span className="text-xs text-yellow-400">
                    first spike: {firstSpikeWeek}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── GDELT Full-Text Coverage ── */}
          {gkgFtResults && gkgFtResults.timeline.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Global news coverage (full-text)
                <span className="text-gray-600 font-normal ml-2">— GDELT keyword search across all indexed articles</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4">
                <BarChart data={gkgFtResults.timeline} xKey="week" yKey="value" height={80} />
                <p className="text-xs text-gray-600 mt-2">
                  Broader than the semantic search above — includes articles that mention the keywords without being semantically similar.
                </p>
              </div>
            </div>
          )}

          {/* ── GDELT TV Mentions ── */}
          {tvResults && tvResults.timeline.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Television news mentions
                <span className="text-gray-600 font-normal ml-2">— US/international broadcast airtime (GDELT TV)</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4">
                <BarChart data={tvResults.timeline} xKey="week" yKey="value" height={80} />
                <p className="text-xs text-gray-600 mt-2">
                  Shows when the narrative crossed from online press into broadcast television coverage.
                </p>
              </div>
            </div>
          )}

          {/* ── Google Trends ── */}
          {trendsResults && trendsResults.timeline.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Google search interest
                <span className="text-gray-600 font-normal ml-2">
                  — public search volume for "{trendsResults.keyword}" (normalized 0–100)
                </span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4">
                <BarChart data={trendsResults.timeline} xKey="week" yKey="value" height={80} />
                <p className="text-xs text-gray-600 mt-2">
                  Compares media coverage spikes above with when the public started searching for this topic.
                  A spike here after a coverage spike suggests media-driven awareness; a spike before suggests organic public interest.
                </p>
              </div>
            </div>
          )}

          {/* ── SEC Filings ── */}
          {secResults && secResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Corporate & Financial Filings (SEC)
                <span className="text-gray-600 font-normal ml-2">— material event disclosures, ownership changes, and annual reports</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-3">
                {secResults.results.map((r, i) => (
                  <div key={i} className="flex justify-between items-start gap-4 text-xs group">
                    <div className="flex-1 min-w-0">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium block truncate">
                        {r.entity}
                      </a>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-400 font-mono text-[10px]">
                          {r.form}
                        </span>
                        <span className="text-gray-600">Material event disclosed</span>
                      </div>
                    </div>
                    <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── WikiLeaks ── */}
          {wikiLeaksResults && wikiLeaksResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Transparency Archives (WikiLeaks)
                <span className="text-gray-600 font-normal ml-2">— matching documents from classified and restricted archives</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-4">
                {wikiLeaksResults.results.map((r, i) => (
                  <div key={i} className="text-xs group">
                    <div className="flex justify-between items-start gap-4 mb-1">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium flex-1 min-w-0">
                        {r.title}
                      </a>
                      <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                    </div>
                    {r.collection && (
                      <span className="inline-block px-1.5 py-0.5 rounded bg-orange-900/30 border border-orange-800/50 text-orange-400 font-mono text-[10px] mb-2">
                        COLLECTION: {r.collection}
                      </span>
                    )}
                    <p className="text-gray-500 leading-relaxed line-clamp-2 italic">
                      "{r.excerpt}"
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── DocumentCloud ── */}
          {docCloudResults && docCloudResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Primary Source Documents (DocumentCloud)
                <span className="text-gray-600 font-normal ml-2">— FOIA records, court filings, and investigative source material</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-3">
                {docCloudResults.results.map((r, i) => (
                  <div key={i} className="flex justify-between items-start gap-4 text-xs group">
                    <div className="flex-1 min-w-0">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium block truncate">
                        {r.title}
                      </a>
                      <div className="flex items-center gap-2 mt-0.5">
                        {r.source && (
                          <span className="text-gray-400">{r.source}</span>
                        )}
                        {r.pageCount && (
                          <span className="text-gray-600">{r.pageCount} pages</span>
                        )}
                      </div>
                    </div>
                    <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Hacker News ── */}
          {hnResults && hnResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Community Discussion (Hacker News)
                <span className="text-gray-600 font-normal ml-2">— technical and geopolitical context from subject-matter experts</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-3">
                {hnResults.results.map((r, i) => (
                  <div key={i} className="flex justify-between items-start gap-4 text-xs group">
                    <div className="flex-1 min-w-0">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium block truncate">
                        {r.title}
                      </a>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-orange-400 font-mono text-[10px]">{r.points} points</span>
                        <span className="text-gray-600">|</span>
                        <span className="text-gray-400">{r.num_comments} comments</span>
                        <span className="text-gray-600">by {r.author}</span>
                      </div>
                    </div>
                    <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── ArXiv ── */}
          {arxivResults && arxivResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Academic & Technical Research (ArXiv)
                <span className="text-gray-600 font-normal ml-2">— preprint scientific papers and deep-tech analysis</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-4">
                {arxivResults.results.map((r, i) => (
                  <div key={i} className="text-xs group">
                    <div className="flex justify-between items-start gap-4 mb-1">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium flex-1 min-w-0">
                        {r.title}
                      </a>
                      <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                    </div>
                    <p className="text-gray-500 leading-relaxed line-clamp-2 italic">
                      {r.snippet}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── 4chan ── */}
          {fourchanResults && fourchanResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Unfiltered Narrative (4chan)
                <span className="text-gray-600 font-normal ml-2">— active imageboard threads from /pol/, /g/, and /biz/</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-4">
                {fourchanResults.results.map((r, i) => (
                  <div key={i} className="text-xs group">
                    <div className="flex justify-between items-start gap-4 mb-1">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-red-400 hover:text-red-300 font-medium flex-1 min-w-0">
                        {r.title}
                      </a>
                      <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-1.5 py-0.5 rounded bg-red-900/20 border border-red-800/30 text-red-500 font-mono text-[10px]">
                        {r.source}
                      </span>
                      <span className="text-gray-600 text-[10px] uppercase tracking-wider">{r.replies} replies | {r.images} images</span>
                    </div>
                    <p className="text-gray-500 leading-relaxed line-clamp-2">
                      {r.snippet}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── GitHub ── */}
          {githubResults && githubResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Technical Repositories & Code (GitHub)
                <span className="text-gray-600 font-normal ml-2">— relevant repositories, tools, and technical discussions</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-3">
                {githubResults.results.map((r, i) => (
                  <div key={i} className="flex justify-between items-start gap-4 text-xs group">
                    <div className="flex-1 min-w-0">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium block truncate">
                        {r.title}
                      </a>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-400 font-mono text-[10px]">
                          {r.type.toUpperCase()}
                        </span>
                        {r.stars !== undefined && (
                          <span className="text-yellow-500 text-[10px]">★ {r.stars}</span>
                        )}
                        {r.language && (
                          <span className="text-gray-500 text-[10px]">{r.language}</span>
                        )}
                        {r.author && (
                          <span className="text-gray-600 italic">by {r.author}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Bluesky ── */}
          {blueskyResults && blueskyResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Real-time Social Narrative (Bluesky)
                <span className="text-gray-600 font-normal ml-2">— trending sentiment and real-time commentary from the AT Protocol network</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-4">
                {blueskyResults.results.map((r, i) => (
                  <div key={i} className="text-xs group">
                    <div className="flex justify-between items-start gap-4 mb-1">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium flex-1 min-w-0">
                        @{r.author}
                      </a>
                      <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                    </div>
                    <p className="text-gray-300 leading-relaxed mb-1">
                      {r.snippet}
                    </p>
                    <div className="flex gap-3 text-[10px] text-gray-600 font-mono uppercase tracking-tighter">
                      <span>{r.likes} likes</span>
                      <span>{r.reposts} reposts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Reddit ── */}
          {redditResults && redditResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Mainstream Social Discussion (Reddit)
                <span className="text-gray-600 font-normal ml-2">— community-driven narratives and trending posts</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-4">
                {redditResults.results.map((r, i) => (
                  <div key={i} className="text-xs group">
                    <div className="flex justify-between items-start gap-4 mb-1">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium flex-1 min-w-0">
                        {r.title}
                      </a>
                      <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-1.5 py-0.5 rounded bg-orange-900/20 border border-orange-800/30 text-orange-500 font-mono text-[10px]">
                        r/{r.subreddit}
                      </span>
                      <span className="text-gray-600 text-[10px] uppercase tracking-wider">{r.score} points | {r.num_comments} comments</span>
                      <span className="text-gray-600 text-[10px]">by {r.author}</span>
                    </div>
                    <p className="text-gray-500 leading-relaxed line-clamp-2 italic">
                      {r.snippet}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Lemmy ── */}
          {lemmyResults && lemmyResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Decentralized Community (Lemmy)
                <span className="text-gray-600 font-normal ml-2">— alternative narratives from the Fediverse</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-4">
                {lemmyResults.results.map((r, i) => (
                  <div key={i} className="text-xs group">
                    <div className="flex justify-between items-start gap-4 mb-1">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium flex-1 min-w-0">
                        {r.title}
                      </a>
                      <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-1.5 py-0.5 rounded bg-purple-900/20 border border-purple-800/30 text-purple-500 font-mono text-[10px]">
                        !{r.community}
                      </span>
                      <span className="text-gray-600 text-[10px] uppercase tracking-wider">Score: {r.score}</span>
                      <span className="text-gray-600 text-[10px]">by {r.author}</span>
                    </div>
                    <p className="text-gray-500 leading-relaxed line-clamp-2 italic">
                      {r.snippet}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── YouTube ── */}
          {youtubeResults && youtubeResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Visual Intelligence & Video (YouTube)
                <span className="text-gray-600 font-normal ml-2">— latest video coverage and eyewitness footage</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-4">
                {youtubeResults.results.map((r, i) => (
                  <div key={i} className="flex gap-4 group">
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-4 mb-1">
                        <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium flex-1 min-w-0">
                          {r.title}
                        </a>
                        <span className="text-gray-500 shrink-0 font-mono text-[10px]">{r.date}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-red-500 font-semibold">{r.author}</span>
                        <span className="text-gray-600 text-[10px]">{r.views ? `${r.views.toLocaleString()} views` : ''}</span>
                        {r.length && (
                          <span className="px-1 py-0.5 rounded bg-gray-800 text-gray-400 text-[9px] font-mono">
                            {Math.floor(r.length / 60)}:{(r.length % 60).toString().padStart(2, '0')}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-[10px] line-clamp-2">
                        {r.snippet}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Federal Register ── */}
          {fedRegResults && fedRegResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Official US Government Actions (Federal Register)
                <span className="text-gray-600 font-normal ml-2">— executive orders, regulations, and public notices</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-3">
                {fedRegResults.results.map((r, i) => (
                  <div key={i} className="flex justify-between items-start gap-4 text-xs group">
                    <div className="flex-1 min-w-0">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium block truncate">
                        {r.title}
                      </a>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="px-1.5 py-0.5 rounded bg-blue-900/20 border border-blue-800/30 text-blue-400 font-mono text-[10px]">
                          {r.type.toUpperCase()}
                        </span>
                        <span className="text-gray-400 truncate">{r.agency}</span>
                      </div>
                    </div>
                    <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Internet Archive ── */}
          {archiveResults && archiveResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Historical Context (Internet Archive)
                <span className="text-gray-600 font-normal ml-2">— digitized books, documents, and historical media</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-4">
                {archiveResults.results.map((r, i) => (
                  <div key={i} className="text-xs group">
                    <div className="flex justify-between items-start gap-4 mb-1">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 font-medium flex-1 min-w-0">
                        {r.title}
                      </a>
                      <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-gray-400">By {r.author}</span>
                      <span className="text-gray-600 italic">({r.mediatype})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── CVE Search ── */}
          {cveResults && cveResults.results?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Cybersecurity Vulnerabilities (CVE)
                <span className="text-gray-600 font-normal ml-2">— technical threat intelligence and software exploits</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4 space-y-3">
                {cveResults.results.map((r, i) => (
                  <div key={i} className="flex justify-between items-start gap-4 text-xs group">
                    <div className="flex-1 min-w-0">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-red-400 hover:text-red-300 font-medium block">
                        {r.title}
                      </a>
                      <p className="text-gray-500 mt-1 line-clamp-1">{r.snippet}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-1.5 py-0.5 rounded bg-red-900/30 border border-red-800/50 text-red-500 font-mono text-[10px]">
                          CVSS: {r.cvss || 'N/A'}
                        </span>
                        <span className="text-gray-600 italic">Target: {r.vendor}</span>
                      </div>
                    </div>
                    <span className="text-gray-500 shrink-0 font-mono">{r.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Events Overlay ── */}
          {eventResults && eventResults.eventTimeline && eventResults.eventTimeline.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                GDELT events
                <span className="text-gray-600 font-normal ml-2">— discrete actor/action events ({eventResults.total} matched)</span>
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded p-4">
                <BarChart data={annotateSpikes(eventResults.eventTimeline)} xKey="week" yKey="count" height={80} />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                {eventResults.topEventTypes?.length > 0 && (
                  <div className="border border-gray-700 rounded p-3 bg-gray-900">
                    <h4 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Top Event Types (CAMEO)</h4>
                    <ol className="space-y-1">
                      {eventResults.topEventTypes.map(({ code, count }, i) => (
                        <li key={i} className="flex justify-between text-xs">
                          <span className="text-orange-400 font-mono">{code}</span>
                          <span className="text-gray-600">{count}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {eventResults.topPairs?.length > 0 && (
                  <div className="border border-gray-700 rounded p-3 bg-gray-900">
                    <h4 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Top Actor Pairs</h4>
                    <ol className="space-y-1">
                      {eventResults.topPairs.map(({ pair, count }, i) => (
                        <li key={i} className="flex justify-between text-xs gap-2">
                          <span className="text-green-400 truncate">{pair}</span>
                          <span className="text-gray-600 shrink-0">{count}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Related Entities ── */}
          {(relatedEntities.persons.length > 0 || relatedEntities.orgs.length > 0 || relatedEntities.themes.length > 0) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Related entities
                <span className="text-gray-600 font-normal ml-2">— most associated persons, organizations, and themes</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: 'People', items: relatedEntities.persons, color: 'text-blue-400' },
                  { label: 'Organizations', items: relatedEntities.orgs, color: 'text-purple-400' },
                  { label: 'Themes', items: relatedEntities.themes, color: 'text-green-400' },
                ].map(({ label, items, color }) => (
                  <div key={label} className="border border-gray-700 rounded p-3 bg-gray-900">
                    <h4 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">{label}</h4>
                    {items.length > 0 ? (
                      <ol className="space-y-1">
                        {items.map(([name, count], i) => (
                          <li key={i} className="flex justify-between items-center">
                            <span className={`text-xs ${color} truncate flex-1`}>{name}</span>
                            <span className="text-xs text-gray-600 ml-2 shrink-0">{count}</span>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="text-xs text-gray-600 italic">none</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Theme Evolution ── */}
          {results.themeEvolution && results.themeEvolution.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Theme evolution
                <span className="text-gray-600 font-normal ml-2">— how coverage focus shifted over time</span>
              </h3>
              <div className={`grid gap-4 ${results.themeEvolution.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`}>
                {results.themeEvolution.map(({ period, dateRange, topThemes }, i) => (
                  <div key={i} className="border border-gray-700 rounded p-3 bg-gray-900">
                    <h4 className="text-xs font-semibold text-gray-400 mb-1 uppercase tracking-wide">{period}</h4>
                    {dateRange && (
                      <div className="text-xs text-gray-600 mb-2 font-mono">{dateRange}</div>
                    )}
                    {topThemes && topThemes.length > 0 ? (
                      <ol className="space-y-1">
                        {topThemes.map(({ theme, count }, j) => (
                          <li key={j} className="flex justify-between items-center gap-2">
                            <span className="text-xs text-orange-400 truncate flex-1">
                              {theme.replace(/_/g, ' ').toLowerCase()}
                            </span>
                            <span className="text-xs text-gray-600 shrink-0">{count}</span>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="text-xs text-gray-600 italic">no theme data</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Geographic Spread ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 mb-3">
              Geographic spread
              <span className="text-gray-600 font-normal ml-2">— by country mentioned in coverage</span>
            </h3>
            {results.byCountry && results.byCountry.length > 0 ? (
              <div className="space-y-2">
                {results.byCountry.map((c, i) => (
                  <HBar
                    key={i}
                    label={countryName(c.country)}
                    sublabel={`first: ${fmtDate(c.first_seen)}`}
                    count={c.count}
                    maxCount={maxCountry}
                  />
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-xs italic">No location data available</p>
            )}
          </div>

          {/* ── Source Origin Geography ── */}
          {results.bySourceCountry && results.bySourceCountry.length > 0 && (() => {
            const maxSrc = Math.max(...results.bySourceCountry.map(c => c.count), 1);
            return (
              <div>
                <h3 className="text-sm font-semibold text-gray-300 mb-3">
                  Coverage origin
                  <span className="text-gray-600 font-normal ml-2">— where outlets publishing this narrative are based</span>
                </h3>
                <div className="space-y-2">
                  {results.bySourceCountry.map((c, i) => (
                    <HBar
                      key={i}
                      label={countryName(c.country)}
                      sublabel={`first: ${fmtDate(c.first_seen)}`}
                      count={c.count}
                      maxCount={maxSrc}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-600 mt-2 italic">
                  Mapped from known outlet domains. Distinct from geographic spread (which shows countries <em>mentioned</em> in coverage).
                </p>
              </div>
            );
          })()}

          {/* ── Source Outlets ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 mb-3">
              Source outlets
              <span className="text-gray-600 font-normal ml-2">— by publication frequency, with first-seen date</span>
            </h3>
            {results.byDomain && results.byDomain.length > 0 ? (
              <div className="space-y-2">
                {results.byDomain.map((d, i) => (
                  <HBar
                    key={i}
                    label={d.domain}
                    sublabel={`first: ${fmtDate(d.first_seen)}`}
                    count={d.count}
                    maxCount={maxDomain}
                  />
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-xs italic">No source data available</p>
            )}
          </div>

          {/* ── Tone Over Time ── */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 mb-3">
              Tone over time
              <span className="text-gray-600 font-normal ml-2">— weekly average sentiment (GDELT scale)</span>
            </h3>
            <div className="bg-gray-900 border border-gray-700 rounded p-4">
              <ToneChart data={results.toneOverTime} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}